import * as React from 'react';
import { statusColors as sharedStatusColors } from '../../shared/styleTokens';

/**
 * Avatar size options
 * - xs: 24px for compact displays
 * - sm: 32px for small displays
 * - md: 40px for standard displays (default)
 * - lg: 48px for larger displays
 * - xl: 64px for prominent displays
 */
export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Avatar status indicator options
 * - online: Green dot indicating active/available
 * - offline: Gray dot indicating inactive/unavailable
 * - busy: Red dot indicating occupied/do not disturb
 * - away: Yellow dot indicating temporarily away
 */
export type AvatarStatus = 'online' | 'offline' | 'busy' | 'away';

/**
 * Props for the Avatar component
 */
export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Image source URL
   */
  src?: string;
  /**
   * Alt text for the image
   */
  alt?: string;
  /**
   * Name used for initials fallback and aria-label
   * If no src provided, initials are derived from this name
   */
  name?: string;
  /**
   * Size of the avatar
   * @default 'md'
   */
  size?: AvatarSize;
  /**
   * Status indicator shown as a dot
   */
  status?: AvatarStatus;
  /**
   * Whether to show a white border (for stacking/overlapping avatars)
   * @default false
   */
  bordered?: boolean;
}

/**
 * Size configuration based on design system specifications
 * Avatar sizes: 24px (xs), 32px (sm), 40px (md), 48px (lg), 64px (xl)
 */
const sizeConfig: Record<AvatarSize, { size: string; text: string; statusSize: string }> = {
  xs: {
    size: 'w-6 h-6',      // 24px
    text: 'text-[10px]',
    statusSize: 'w-1.5 h-1.5', // 6px (25% of 24px)
  },
  sm: {
    size: 'w-8 h-8',      // 32px
    text: 'text-[11px]',
    statusSize: 'w-2 h-2', // 8px (25% of 32px)
  },
  md: {
    size: 'w-10 h-10',    // 40px
    text: 'text-[13px]',
    statusSize: 'w-2.5 h-2.5', // 10px (25% of 40px)
  },
  lg: {
    size: 'w-12 h-12',    // 48px
    text: 'text-sm',
    statusSize: 'w-3 h-3', // 12px (25% of 48px)
  },
  xl: {
    size: 'w-16 h-16',    // 64px
    text: 'text-base',
    statusSize: 'w-4 h-4', // 16px (25% of 64px)
  },
};

/**
 * Status color configuration based on design system (shared tokens)
 */
const statusColors: Record<AvatarStatus, string> = sharedStatusColors;

/**
 * Extracts initials from a name
 * - For "John Doe" returns "JD"
 * - For "John" returns "JO"
 * - For empty/undefined returns "?"
 */
function getInitials(name?: string): string {
  if (!name || name.trim() === '') {
    return '?';
  }

  const parts = name.trim().split(/\s+/);

  if (parts.length === 1) {
    // Single name: take first two letters
    /* v8 ignore start */
    const singleName = parts[0];
    return singleName ? singleName.substring(0, 2).toUpperCase() : '?';
    /* v8 ignore stop */
  }

  // Multiple names: take first letter of first and last name
  /* v8 ignore start */
  const firstName = parts[0];
  const lastName = parts.at(-1);
  const first = firstName ? firstName[0] ?? '' : '';
  const last = lastName ? lastName[0] ?? '' : '';
  return (first + last).toUpperCase();
  /* v8 ignore stop */
}

/**
 * Avatar component for user/entity representation.
 *
 * Displays either an image or initials fallback with optional status indicator.
 * Designed for insurance industry applications where user identification is important.
 *
 * @example
 * // With image
 * <Avatar src="/user.jpg" name="John Doe" />
 *
 * @example
 * // With initials fallback
 * <Avatar name="Jane Smith" size="lg" />
 *
 * @example
 * // With status indicator
 * <Avatar name="Alice" status="online" />
 *
 * @example
 * // Bordered for stacking
 * <Avatar name="Bob" bordered />
 */
const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      src,
      alt,
      name,
      size = 'md',
      status,
      bordered = false,
      className = '',
      ...props
    },
    ref
  ) => {
    const [imageError, setImageError] = React.useState(false);

    // Reset error state when src changes
    React.useEffect(() => {
      setImageError(false);
    }, [src]);

    const config = sizeConfig[size];
    const initials = getInitials(name);
    const showImage = src && !imageError;
    const ariaLabel = name || alt || 'Avatar';

    // Container classes
    const containerClasses = [
      // Base styles
      'relative inline-flex items-center justify-center',
      'rounded-full overflow-hidden',
      'flex-shrink-0',
      // Size
      config.size,
      // Border for stacking
      bordered ? 'ring-2 ring-white' : '',
      // Custom classes
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Fallback container classes (for initials)
    const fallbackClasses = [
      'w-full h-full',
      'flex items-center justify-center',
      'bg-primary-100',
      'text-primary-700',
      'font-medium',
      config.text,
    ].join(' ');

    return (
      <div
        ref={ref}
        className={containerClasses}
        aria-label={ariaLabel}
        {...props}
      >
        {showImage ? (
          <img
            src={src}
            alt={alt || name || ''}
            className="w-full h-full object-cover"
            onError={() => { setImageError(true); }}
          />
        ) : (
          <span className={fallbackClasses} aria-hidden="true">
            {initials}
          </span>
        )}

        {status && (
          <span
            className={[
              'absolute bottom-0 right-0',
              'rounded-full',
              'ring-2 ring-white',
              config.statusSize,
              statusColors[status],
            ].join(' ')}
            aria-label={`Status: ${status}`}
          />
        )}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';

export { Avatar, getInitials };
